// src/components/events/EventBinding.jsx

import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../../firebase";
import { doc, onSnapshot, updateDoc, increment } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import EventDescription from "./EventDescription";
import { FaEye } from "react-icons/fa";

export default function EventBinding() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [heroUrl, setHeroUrl] = useState("");
  const hasIncremented = useRef(false);

  // 1) Firestore 실시간 구독
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "events", eventId), (snap) => {
      if (snap.exists()) {
        setEvent({ id: snap.id, ...snap.data() });
      }
    });
    return () => unsub();
  }, [eventId]);

  // 2) heroImagePath 가 바뀔 때마다, storage 에서 HTTP URL 가져오기 또는 그대로 사용
  useEffect(() => {
    if (!event) return;

    const storedPath = event.heroImagePath || "";
    if (!storedPath) {
      setHeroUrl("");
      return;
    }

    // 2-1) “HTTP URL”인지 검사
    if (storedPath.startsWith("http://") || storedPath.startsWith("https://")) {
      setHeroUrl(storedPath);
      return;
    }

    // 2-2) 스토리지 내부 경로인 경우 getDownloadURL 호출
    const storage = getStorage();
    getDownloadURL(ref(storage, storedPath))
      .then((url) => setHeroUrl(url))
      .catch(() => setHeroUrl(""));
  }, [event]);

  // 3) 컴포넌트 마운트 시 조회수 1 증가 (한 번만 실행)
  useEffect(() => {
    if (!eventId || hasIncremented.current) return;

    hasIncremented.current = true;
    const viewCountRef = doc(db, "events", eventId);
    updateDoc(viewCountRef, {
      viewCount: increment(1),
    }).catch((err) => {
      console.error("viewCount 업데이트 실패:", err);
    });
  }, [eventId]);

  if (!event) {
    return <div className="py-20 text-center text-gray-500">로딩 중...</div>;
  }

  // 상태값, D-Day 계산
  const now = new Date();
  const start = new Date(event.startDate.replace(/\./g, "-") + "T00:00:00");
  const end = new Date(event.endDate.replace(/\./g, "-") + "T00:00:00");

  let statusLabel, statusColor;
  if (now < start) {
    statusLabel = "진행예정";
    statusColor = "bg-purple-600";
  } else if (now > end) {
    statusLabel = "종료";
    statusColor = "bg-gray-600";
  } else {
    statusLabel = "진행중";
    statusColor = "bg-green-500";
  }

  const msPerDay = 1000 * 60 * 60 * 24;
  const daysLeftRaw = (end - now) / msPerDay;
  const daysLeft = Math.ceil(daysLeftRaw);

  return (
    <div className="bg-[#121212] text-white min-h-screen">
      {/* ─── 배너(히어로) ─── */}
      <div
        className="relative h-[440px] bg-center bg-cover"
        style={{ backgroundImage: `url(${heroUrl})` }}
      >
        <div className="absolute inset-0 bg-black/20" />

        <div className="relative z-10 flex h-full flex-col justify-center max-w-7xl mx-auto px-6 space-y-2">
          <div className="flex items-center space-x-4 text-sm">
            <span
              className={`px-2 py-1 rounded text-sm font-medium ${statusColor}`}
            >
              {statusLabel}
            </span>
            {statusLabel === "진행중" && daysLeft > 0 && daysLeft <= 4 && (
              <span className="px-2 py-1 rounded text-sm font-medium bg-red-500">
                D-{daysLeft}
              </span>
            )}
            <span className="text-sm font-semibold">
              {event.startDate} ~ {event.endDate} (UTC+9)
            </span>
            <span className="flex items-center">
              <FaEye className="mr-2" />
              {event.viewCount || 0}
            </span>
          </div>

          <h1 className="text-5xl font-extrabold leading-snug">
            {event.title}
          </h1>

          {event.subtitle && (
            <p className="text-lg font-semibold text-gray-200 max-w-3xl">
              {event.subtitle}
            </p>
          )}
        </div>
      </div>

      {/* ─── 탭 네비게이션 ─── */}
      <div className="bg-[#1f1f1f] border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6">
          <ul className="flex justify-center space-x-8 text-sm">
            <li className="border-b-2 border-white py-4 text-white font-medium">
              이벤트 정보
            </li>
          </ul>
        </div>
      </div>

      {/* ─── 본문(Description) ─── */}
      <div className="container mx-auto px-6 py-8 pb-12 max-w-7xl">
        <EventDescription content={event.content} />
      </div>
    </div>
  );
}
